import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationContext } from './NavigationContext';
import { toApiError } from '@/api/errorHandler';
import { notifyGlobal } from '@/context/notificationContext';
import {
  navigationQueryKeys,
  getNavigationTree,
  saveNavigationTree,
} from '@/navigation/NavigationService';
import {
  buildNavigationRows,
  filterNavigationRows,
} from '@/navigation/NavigationTreeBuilder';
import type {
  NavigationEngineState,
  NavigationNode,
} from '@/navigation/NavigationTypes';
import {
  appendNavigationNode,
  collectNodeIds,
  createNavigationNode,
  deleteNavigationNode,
  duplicateNavigationNode,
  moveNavigationNode,
  updateNavigationNode,
  validateNavigationTree,
} from '@/utils/navigationTreeUtils';

function findNodeById(
  nodes: NavigationNode[],
  nodeId: string | null,
): NavigationNode | undefined {
  if (!nodeId) {
    return undefined;
  }

  for (const node of nodes) {
    if (node.id === nodeId) {
      return node;
    }

    const child = findNodeById(node.children, nodeId);

    if (child) {
      return child;
    }
  }

  return undefined;
}

function findNodeExists(nodes: NavigationNode[], nodeId: string): boolean {
  return Boolean(findNodeById(nodes, nodeId));
}

function findFirstNodeId(nodes: NavigationNode[]): string | null {
  return nodes[0]?.id ?? null;
}

export function useNavigationEngine(
  productId: string | undefined,
): NavigationEngineState {
  const queryClient = useQueryClient();
  const { t } = useTranslation('entity-portal');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [localNodes, setLocalNodes] = useState<NavigationNode[]>([]);
  const [treeSearch, setTreeSearch] = useState('');

  const navigationQuery = useQuery({
    queryKey: navigationQueryKeys.tree(productId ?? ''),
    queryFn: () => getNavigationTree(productId ?? ''),
    enabled: Boolean(productId),
  });

  useEffect(() => {
    if (navigationQuery.data?.nodes) {
      setLocalNodes(navigationQuery.data.nodes);
      setExpandedIds(new Set(collectNodeIds(navigationQuery.data.nodes)));
      setSelectedNodeId((current) => {
        if (current && findNodeExists(navigationQuery.data.nodes, current)) {
          return current;
        }

        return findFirstNodeId(navigationQuery.data.nodes);
      });
      return;
    }

    setLocalNodes([]);
    setSelectedNodeId(null);
    setExpandedIds(new Set());
  }, [navigationQuery.data?.nodes, productId]);

  const saveMutation = useMutation({
    mutationFn: () => saveNavigationTree(productId ?? '', localNodes),
    onSuccess: (data) => {
      queryClient.setQueryData(navigationQueryKeys.tree(productId ?? ''), data);
      notifyGlobal(t('product.menuUpdatedSuccess'), 'success');
    },
    onError: (error) => {
      notifyGlobal(toApiError(error, t).message, 'error');
    },
  });

  const selectedNode = useMemo(
    () => findNodeById(localNodes, selectedNodeId),
    [localNodes, selectedNodeId],
  );

  const validationIssues = useMemo(
    () => validateNavigationTree(localNodes),
    [localNodes],
  );

  const filteredRows = useMemo(() => {
    const rows = buildNavigationRows(localNodes, expandedIds);
    return filterNavigationRows(rows, treeSearch);
  }, [expandedIds, localNodes, treeSearch]);

  const updateSelectedNode = useCallback(
    (updates: Partial<NavigationNode>) => {
      if (!selectedNodeId) {
        return;
      }

      setLocalNodes((current) =>
        updateNavigationNode(current, selectedNodeId, (node) => ({
          ...node,
          ...updates,
        })),
      );
    },
    [selectedNodeId],
  );

  const addGroup = useCallback(() => {
    const node = createNavigationNode('group', null);
    setLocalNodes((current) => appendNavigationNode(current, node, null));
    setSelectedNodeId(node.id);
    setExpandedIds((current) => new Set([...current, node.id]));
  }, []);

  const addItem = useCallback(() => {
    const parentId =
      selectedNode?.type === 'group'
        ? selectedNode.id
        : (selectedNode?.parentId ?? null);
    const node = createNavigationNode('item', parentId);
    setLocalNodes((current) => appendNavigationNode(current, node, parentId));
    setSelectedNodeId(node.id);

    if (parentId) {
      setExpandedIds((current) => new Set([...current, parentId]));
    }
  }, [selectedNode]);

  const removeSelectedNode = useCallback(() => {
    if (!selectedNodeId) {
      return;
    }

    setLocalNodes((current) => deleteNavigationNode(current, selectedNodeId));
    setSelectedNodeId(null);
  }, [selectedNodeId]);

  const moveNode = useCallback(
    (
      dragId: string,
      targetId: string,
      position: 'before' | 'after' | 'inside',
    ) => {
      setLocalNodes((current) =>
        moveNavigationNode(current, dragId, targetId, position),
      );
    },
    [],
  );

  const toggleExpanded = useCallback((nodeId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);

      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }

      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(collectNodeIds(localNodes)));
  }, [localNodes]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  const createRootMenu = useCallback(() => {
    addGroup();
  }, [addGroup]);

  const saveChanges = useCallback(async () => {
    if (!productId) {
      return;
    }

    await saveMutation.mutateAsync();
  }, [productId, saveMutation]);

  const updateNode = useCallback(
    (nodeId: string, updates: Partial<NavigationNode>) => {
      setLocalNodes((current) =>
        updateNavigationNode(current, nodeId, (node) => ({
          ...node,
          ...updates,
        })),
      );
    },
    [],
  );

  const deleteNode = useCallback((nodeId: string) => {
    setLocalNodes((current) => deleteNavigationNode(current, nodeId));
    setSelectedNodeId((current) => (current === nodeId ? null : current));
  }, []);

  const duplicateNode = useCallback((nodeId: string) => {
    setLocalNodes((current) => duplicateNavigationNode(current, nodeId));
  }, []);

  return {
    nodes: localNodes,
    selectedNodeId,
    selectedNode,
    expandedIds,
    treeSearch,
    filteredRows,
    validationIssues,
    isLoading: navigationQuery.isLoading,
    isSaving: saveMutation.isPending,
    isError: navigationQuery.isError,
    error: navigationQuery.error,
    setSelectedNodeId,
    setTreeSearch,
    updateSelectedNode,
    addGroup,
    addItem,
    removeSelectedNode,
    moveNode,
    toggleExpanded,
    expandAll,
    collapseAll,
    createRootMenu,
    saveChanges,
    refetch: navigationQuery.refetch,
    updateNode,
    deleteNode,
    duplicateNode,
  };
}

export function useNavigation(): NavigationEngineState {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigation must be used inside NavigationProvider');
  }

  return context;
}
